import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import { Button, Text, Modal, Banner, Checkbox } from '@shopify/polaris';

import BatchGrid from '../../partials/BatchGrid';
import LoadingSpinner from '../../partials/LoadingSpinner';
import { useGetBatchesQuery, useStartPackingMutation, useResumePackingMutation } from '../../../warehouse/store/batches';
import { getSessionEmail } from '../../../warehouse/utils/session';
import { isAuthError, SESSION_EXPIRED_MESSAGE } from '../../../warehouse/utils/errors';
import { useRequirePermission } from '../../../warehouse/utils/permissions';
import { UI_COLORS } from '../../../constants';
import { resetPackStateForBatch } from '../../../warehouse/data/packedShipments';
import { buttonProps, useDocumentTitle } from '../../../warehouse/utils/a11y';

// Totes are filled one row at a time, top to bottom, so the first
// toteCount cells in row-major order are the ones in use. Totes whose
// shipment was marked unpickable show the warning colour instead - they
// can't be packed.
const USED_TOTE_COLOR = UI_COLORS.blue;
const UNPICKABLE_TOTE_COLOR = UI_COLORS.accent;

const getToteHighlights = (batch) => {
  const highlights = new Map(
    Array.from({ length: batch.toteCount }, (_, i) => {
      const row = Math.floor(i / batch.totalColumns) + 1;
      const column = (i % batch.totalColumns) + 1;
      return [`${row}-${column}`, { row, column, color: USED_TOTE_COLOR }];
    })
  );

  (batch.unpickableTotes ?? []).forEach(tote => {
    highlights.set(`${tote.row}-${tote.column}`, {
      row: tote.row,
      column: tote.column,
      color: UNPICKABLE_TOTE_COLOR,
    });
  });

  return [...highlights.values()];
};

const PackingBatches = () => {
  useDocumentTitle('Packing');
  const navigate = useNavigate();
  // Redirects to Profile when the user lacks the packer permission
  const { loaded: permissionsLoaded, allowed } = useRequirePermission('packer');
  const [search, setSearch] = useState('');
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const searchInputRef = useRef(null);

  // The parked switch swaps the list from the packing queue to batches
  // parked mid-pack - those can be claimed and resumed by any packer
  const [showParked, setShowParked] = useState(false);

  // Picked batches wait for a packer; packing batches show up read-only
  // so a packer sees what's being worked on - and can re-enter their own
  const {
    data: picked = [],
    isLoading: pickedLoading,
    error: pickedError,
    isError: isPickedError,
    refetch: refetchPicked,
  } = useGetBatchesQuery('picked', { skip: showParked });
  const {
    data: packing = [],
    isLoading: packingLoading,
    refetch: refetchPacking,
  } = useGetBatchesQuery('packing', { skip: showParked });
  const {
    data: parked = [],
    isLoading: parkedLoading,
    error: parkedError,
    isError: isParkedError,
    refetch: refetchParked,
  } = useGetBatchesQuery('packing_parked', { skip: !showParked });
  const [startPacking, { isLoading: isClaiming }] = useStartPackingMutation();
  const [resumePacking, { isLoading: isResuming }] = useResumePackingMutation();

  // A batch can briefly appear in both lists when a claim lands before
  // the picked query refreshes - the packing entry is fresher, so it wins
  const batches = showParked
    ? parked
    : Array.from(
        new Map([...picked, ...packing].map((batch) => [batch.id, batch])).values()
      );
  const isLoading = showParked ? parkedLoading : pickedLoading || packingLoading;
  const listQueryError = showParked ? parkedError : pickedError;
  const isListQueryError = showParked ? isParkedError : isPickedError;

  const refetch = useCallback(() => {
    if (showParked) {
      refetchParked();
    } else {
      refetchPicked();
      refetchPacking();
    }
  }, [showParked, refetchParked, refetchPicked, refetchPacking]);

  // The logged-in user's email, for spotting our own in-progress batches
  const [myEmail, setMyEmail] = useState('');
  useEffect(() => {
    getSessionEmail().then(email => setMyEmail(email || ''));
  }, []);

  // Web routes remount when navigated to - refresh on mount/toggle
  useEffect(() => {
    refetch();
  }, [refetch]);

  // Batch pending start-packing confirmation
  const [confirmBatch, setConfirmBatch] = useState(null);
  // Parked batch pending resume confirmation
  const [resumeBatch, setResumeBatch] = useState(null);
  const [listError, setListError] = useState('');

  // Auto-focus the search input after 1s of inactivity so a scanner can
  // dump a batch/order/cart id straight in
  useEffect(() => {
    if (isSearchFocused) return;
    const timer = setTimeout(() => {
      searchInputRef.current?.focus();
    }, 1000);
    return () => clearTimeout(timer);
  }, [isSearchFocused, search]);

  // Partial, case-insensitive match on batch id/name, cart id, or any order id
  const query = search.trim().toLowerCase();
  const filteredBatches = query
    ? batches.filter(
        b =>
          b.id.toLowerCase().includes(query) ||
          b.name.toLowerCase().includes(query) ||
          (b.cartId || '').toLowerCase().includes(query) ||
          b.items.some(i => i.orderShipmentId.toLowerCase().includes(query))
      )
    : batches;

  const handleBatchPress = (batch) => {
    if (batch.status === 'packing_parked') {
      // Any packer can claim a parked batch and pick the work back up
      setResumeBatch(batch);
      return;
    }
    if (batch.status === 'packing') {
      // Already being packed - only the packer who claimed it can re-enter
      if (batch.packer !== myEmail || !myEmail) return;
      navigate(`/warehouse/app/packing/${batch.id}`);
      return;
    }
    setConfirmBatch(batch);
  };

  // Ask the backend to check the batch out to this packer. It only
  // succeeds while the batch is still 'picked' and nobody else is
  // packing it.
  const handleConfirmPacking = async () => {
    if (!confirmBatch || isClaiming) return;

    const result = await startPacking(confirmBatch.id);

    if ('error' in result) {
      const status = result.error?.status;
      setConfirmBatch(null);
      setListError(
        status === 401
          ? SESSION_EXPIRED_MESSAGE
          : status === 409
            ? `${confirmBatch.name} is already being packed by someone else.`
            : `Could not start packing ${confirmBatch.name}. Please try again.`
      );
      refetch();
      return;
    }

    setConfirmBatch(null);
    setListError('');
    // A fresh claim starts with clean pack state - never carry over
    // another batch's packed/unpackable marks
    resetPackStateForBatch(confirmBatch.id);
    navigate(`/warehouse/app/packing/${confirmBatch.id}`);
  };

  // Claiming a parked batch checks it out to this packer and flips it
  // back to 'packing'; the saved packing_state is restored on the screen
  const handleConfirmResume = async () => {
    if (!resumeBatch || isResuming) return;

    const result = await resumePacking(resumeBatch.id);

    if ('error' in result) {
      const status = result.error?.status;
      setResumeBatch(null);
      setListError(
        status === 401
          ? SESSION_EXPIRED_MESSAGE
          : status === 409
            ? `${resumeBatch.name} is no longer parked - it may have been claimed or completed already.`
            : `Could not resume packing ${resumeBatch.name}. Please try again.`
      );
      refetch();
      return;
    }

    setResumeBatch(null);
    setListError('');
    navigate(`/warehouse/app/packing/${resumeBatch.id}`);
  };

  if (!permissionsLoaded || !allowed) {
    return null;
  }

  const renderBatchCard = (batch) => {
    const shipmentIds = [...new Set(batch.items.map(i => i.orderShipmentId))];
    const unpickableCount = (batch.unpickableTotes ?? []).length;
    const isPacking = batch.status === 'packing';
    const isParked = batch.status === 'packing_parked';
    const isMine = isPacking && myEmail !== '' && batch.packer === myEmail;
    const disabled = isPacking && !isMine;

    return (
      <div
        key={batch.id}
        className={`warehouse-batch-card${disabled ? ' warehouse-batch-card-disabled' : ''}`}
        {...buttonProps(() => handleBatchPress(batch), { disabled })}
      >
        <div className="warehouse-batch-header">
          <Text as="p" fontWeight="bold">{batch.name}</Text>
        </div>

        <div className="warehouse-batch-statuses">
          {isParked ? (
            <span className="warehouse-badge warehouse-badge-warning">⏸ PARKED</span>
          ) : isPacking ? (
            <span className="warehouse-badge warehouse-badge-primary">
              {isMine ? '▶ PACKING - RESUME' : 'PACKING'}
            </span>
          ) : (
            <span className="warehouse-badge warehouse-badge-success">✓ PICKED</span>
          )}
          {unpickableCount > 0 && (
            <span className="warehouse-badge warehouse-badge-accent">
              ⚠ {unpickableCount} UNPICKABLE TOTE{unpickableCount === 1 ? '' : 'S'}
            </span>
          )}
        </div>

        <div className="warehouse-batch-info">
          <span className="warehouse-info-label">Batch ID:</span>
          <span>{batch.id}</span>
        </div>

        <div className="warehouse-batch-info">
          <span className="warehouse-info-label">Cart:</span>
          <span>{batch.cartId}</span>
        </div>

        <div className="warehouse-batch-info">
          <span className="warehouse-info-label">Items:</span>
          <span>{batch.items.length}</span>
        </div>

        {isPacking && (
          <div className="warehouse-batch-info">
            <span className="warehouse-info-label">Packer:</span>
            <span>{batch.packer}</span>
          </div>
        )}

        {isParked && batch.packingState?.packer_email ? (
          <div className="warehouse-batch-info">
            <span className="warehouse-info-label">Parked by:</span>
            <span>{batch.packingState.packer_email}</span>
          </div>
        ) : null}

        <div className="warehouse-batch-preview">
          <span className="warehouse-info-label">Shipments in batch:</span>
          <span>
            {shipmentIds.slice(0, 3).join(', ')}
            {shipmentIds.length > 3 ? ` +${shipmentIds.length - 3} more` : ''}
          </span>
        </div>

        <div className="warehouse-batch-preview">
          <span className="warehouse-info-label">Cart totes:</span>
          <BatchGrid
            totalRows={batch.totalRows}
            totalColumns={batch.totalColumns}
            toteCount={batch.toteCount}
            toteSize={24}
            gap={4}
            highlightedTotes={getToteHighlights(batch)}
          />
        </div>
      </div>
    );
  };

  return (
    <div className="warehouse-screen">
      <div className="warehouse-header">
        <div className="warehouse-header-row">
          <div>
            <h2>Packing</h2>
            <div className="warehouse-header-subtitle">
              {showParked
                ? `${batches.length} parked packing batches`
                : `${batches.length} batches ready for packing`}
            </div>
          </div>
          <div className="warehouse-header-actions">
            <div className="warehouse-switch-label">
              Parked
              <Checkbox checked={showParked} onChange={setShowParked} label="Show parked batches" labelHidden />
            </div>
            <Button onClick={refetch} disabled={isLoading} loading={isLoading}>
              Refresh
            </Button>
          </div>
        </div>
      </div>

      <div className="warehouse-scanner">
        <div className={`warehouse-scan-indicator${isSearchFocused ? ' warehouse-scan-indicator-ready' : ''}`} role="status">
          {isSearchFocused ? 'Ready to scan' : 'Not ready to scan'}
        </div>
        <input
          ref={searchInputRef}
          className={`warehouse-barcode-input${isSearchFocused ? ' warehouse-barcode-input-focused' : ''}`}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onFocus={() => setIsSearchFocused(true)}
          onBlur={() => setIsSearchFocused(false)}
          placeholder="Search by batch, cart, or order ID"
          aria-label="Search batches by batch, cart, or order ID"
          autoCapitalize="none"
          autoCorrect="off"
        />
      </div>

      {listError ? (
        <Banner tone="critical"><p>{listError}</p></Banner>
      ) : null}

      {isListQueryError ? (
        <Banner tone="critical">
          <p>
            {isAuthError(listQueryError)
              ? SESSION_EXPIRED_MESSAGE
              : showParked
                ? 'Could not load parked batches. Press refresh to try again.'
                : 'Could not load batches. Press refresh to try again.'}
          </p>
        </Banner>
      ) : null}

      <div className="warehouse-list">
        {isLoading && batches.length === 0 ? (
          <div className="warehouse-loading"><LoadingSpinner statusText="Loading batches..." /></div>
        ) : filteredBatches.length === 0 ? (
          <div className="warehouse-loading">
            <Text as="p">{showParked ? 'No parked packing batches.' : 'No batches ready for packing.'}</Text>
          </div>
        ) : (
          filteredBatches.map(renderBatchCard)
        )}
      </div>

      <Modal
        open={confirmBatch !== null}
        onClose={() => setConfirmBatch(null)}
        title="Start Packing?"
      >
        {confirmBatch && (
          <Modal.Section>
            <Text as="p" fontWeight="bold">{confirmBatch.name}</Text>
            <Text as="p">
              Confirm that cart <strong>{confirmBatch.cartId || 'unknown'}</strong> is
              in front of you, with {confirmBatch.toteCount} tote{confirmBatch.toteCount === 1 ? '' : 's'} in
              the positions shown below.
            </Text>
            <div className="warehouse-popup-grid">
              <BatchGrid
                totalRows={confirmBatch.totalRows}
                totalColumns={confirmBatch.totalColumns}
                toteCount={confirmBatch.toteCount}
                toteSize={44}
                gap={8}
                highlightedTotes={getToteHighlights(confirmBatch)}
              />
            </div>
            {isClaiming ? (
              <div className="warehouse-loading"><LoadingSpinner statusText="Checking out batch..." /></div>
            ) : (
              <div className="warehouse-popup-buttons">
                <Button onClick={() => setConfirmBatch(null)}>Cancel</Button>
                <Button variant="primary" onClick={handleConfirmPacking}>Start Packing</Button>
              </div>
            )}
          </Modal.Section>
        )}
      </Modal>

      <Modal
        open={resumeBatch !== null}
        onClose={() => setResumeBatch(null)}
        title="Resume Packing?"
      >
        {resumeBatch && (
          <Modal.Section>
            <Text as="p" fontWeight="bold">{resumeBatch.name}</Text>
            <Text as="p">
              Check out this parked batch and continue packing cart{' '}
              <strong>{resumeBatch.cartId || 'unknown'}</strong> from where it was left off.
            </Text>
            <div className="warehouse-popup-grid">
              <BatchGrid
                totalRows={resumeBatch.totalRows}
                totalColumns={resumeBatch.totalColumns}
                toteCount={resumeBatch.toteCount}
                toteSize={44}
                gap={8}
                highlightedTotes={getToteHighlights(resumeBatch)}
              />
            </div>
            {isResuming ? (
              <div className="warehouse-loading"><LoadingSpinner statusText="Checking out batch..." /></div>
            ) : (
              <div className="warehouse-popup-buttons">
                <Button onClick={() => setResumeBatch(null)}>Cancel</Button>
                <Button variant="primary" onClick={handleConfirmResume}>Resume Packing</Button>
              </div>
            )}
          </Modal.Section>
        )}
      </Modal>
    </div>
  );
};

export default PackingBatches;
